import React, { useState, useRef, useEffect } from "react";
import { Check, X, Upload } from "lucide-react";
import InlineSpinner from "../shared/Spinner/InlineSpinner";
import { uploadFile, downloadImageBlob } from "../../api/providers";
import { ALLOWED_FILE_ACCEPT_STRING } from "../../utils/helpers";

/**
 * Compact inline file upload component
 * Supports drag & drop and click to browse
 * For icon uploads, shows image preview inline
 */
const FileUploadInput = ({
  orgId,
  uploadType,      // 'provider_icon', 'agent_icon', 'scopes', 'agent_policy', 'agent_tools', 'agent_prompt'
  entityId,        // provider_id or agent_id (can be empty initially)
  providerId,      // Optional: provider_id for agent file uploads (required for agent_policy, agent_tools, agent_prompt)
  onUploadComplete,// Callback with { file_path, filename }
  accept,          // File types to accept (e.g., ".svg,.png,.json")
  initialFilePath, // Optional: pre-existing file path (shows as already uploaded)
  placeholder = "Drop file or click to browse",
  className = "",
  deferUpload = false,
  onFileSelected,
  disabled = false,
  inputLabel = "Choose file",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const fileInputRef = useRef(null);
  const previewObjectUrlRef = useRef(null);

  const isIconUpload = uploadType === "provider_icon" || uploadType === "agent_icon";

  useEffect(() => {
    if (!initialFilePath) return;

    setUploadedFile({
      filename: initialFilePath.split('/').pop(),
      file_path: initialFilePath,
    });
  }, [initialFilePath]);

  useEffect(() => {
    if (!initialFilePath || !isIconUpload || !orgId) return undefined;

    let cancelled = false;
    setLoadingPreview(true);
    setError(null);

    downloadImageBlob(initialFilePath, orgId)
      .then((blob) => {
        if (cancelled) return;
        if (previewObjectUrlRef.current) {
          URL.revokeObjectURL(previewObjectUrlRef.current);
        }
        const objectUrl = URL.createObjectURL(blob);
        previewObjectUrlRef.current = objectUrl;
        setImagePreview(objectUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || "Failed to load icon preview");
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialFilePath, isIconUpload, orgId]);

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
    };
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileSelect = (e) => {
    if (disabled) return;
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file) => {
    if (deferUpload) {
      if (isIconUpload) {
        const allowedTypes = ["image/svg+xml", "image/png", "image/jpeg", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
          setError("Icon must be SVG, PNG, JPG or WebP");
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          setError("Icon must be 2 MB or smaller");
          return;
        }
      }

      setError(null);
      setUploadedFile({ filename: file.name });
      if (isIconUpload) {
        if (previewObjectUrlRef.current) {
          URL.revokeObjectURL(previewObjectUrlRef.current);
        }
        const objectUrl = URL.createObjectURL(file);
        previewObjectUrlRef.current = objectUrl;
        setImagePreview(objectUrl);
      }
      onFileSelected?.(file);
      return;
    }

    if (!entityId) {
      setError("Please enter an ID first");
      return;
    }

    // For agent file uploads, require providerId
    const agentFileTypes = ["agent_policy", "agent_tools", "agent_prompt"];
    if (agentFileTypes.includes(uploadType) && !providerId) {
      setError("Please select a provider first");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const result = await uploadFile(file, orgId, uploadType, entityId, providerId);
      setUploadedFile({
        filename: result.filename,
        file_path: result.file_path,
      });
      
      // For icon uploads, create preview from uploaded file
      if (isIconUpload) {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (previewObjectUrlRef.current) {
            URL.revokeObjectURL(previewObjectUrlRef.current);
            previewObjectUrlRef.current = null;
          }
          setImagePreview(reader.result); // Store data URL directly
        };
        reader.readAsDataURL(file);
      }
      
      onUploadComplete?.(result);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    if (disabled) return;
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
    setUploadedFile(null);
    setError(null);
    setImagePreview(null);
    if (deferUpload) {
      onFileSelected?.(null);
    } else {
      onUploadComplete?.(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  // Icon upload - single box design
  if (isIconUpload) {
    const hasIcon = imagePreview && !loadingPreview;
    
    return (
      <div className={className}>
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          aria-disabled={disabled}
          className={`relative w-16 h-16 border rounded-lg flex items-center justify-center transition-colors overflow-hidden ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"} ${
            isDragging
              ? "border-edge-primary bg-fill-primary/5"
              : "border-edge-gray-300 dark:border-edge-gray-600 bg-fill-gray-50 dark:bg-fill-deep-surface hover:border-edge-primary"
          }`}
        >
          {isUploading ? (
            <InlineSpinner size="md" className="text-ink-primary" />
          ) : loadingPreview ? (
            <InlineSpinner size="md" className="text-ink-gray-400" />
          ) : hasIcon ? (
            <img
              src={imagePreview}
              alt="Icon preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <Upload className={`w-5 h-5 ${isDragging ? "text-ink-primary" : "text-ink-gray-400"}`} />
          )}
          
          {/* X button at bottom right */}
          {hasIcon && (
            <button
              type="button"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="absolute bottom-0.5 right-0.5 w-4 h-4 bg-fill-red-500 rounded-full flex items-center justify-center text-ink-on-color hover:bg-fill-red-600 transition-colors shadow-sm"
              title="Remove icon"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
        
        <input
          aria-label={inputLabel}
          ref={fileInputRef}
          type="file"
          disabled={disabled}
          accept={accept || ALLOWED_FILE_ACCEPT_STRING}
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {error && (
          <p className="text-xs text-ink-red-500 mt-1">{error}</p>
        )}
      </div>
    );
  }

  // Uploaded state (non-icon files)
  if (uploadedFile) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex-1 flex items-center gap-2 px-4 py-2.5 border border-edge-green-300 dark:border-edge-green-600 rounded-lg bg-fill-green-50 dark:bg-fill-green-900/20">
          <Check className="w-4 h-4 text-ink-green-500 flex-shrink-0" />
          <span className="text-sm text-ink-green-700 dark:text-ink-green-400 truncate">
            {uploadedFile.filename}
          </span>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          className="p-2 text-ink-gray-400 hover:text-ink-red-500 transition-colors"
          title="Remove file"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Uploading state
  if (isUploading) {
    return (
      <div className={`flex items-center gap-2 px-4 py-2.5 border border-edge-gray-300 dark:border-edge-gray-600 rounded-lg bg-fill-gray-50 dark:bg-fill-deep-surface ${className}`}>
        <InlineSpinner className="text-ink-primary flex-shrink-0" />
        <span className="text-sm text-ink-gray-500">Uploading...</span>
      </div>
    );
  }

  // Default state - input
  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleClick();
          }
        }}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        aria-disabled={disabled}
        className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"} ${
          isDragging
            ? "border-edge-primary bg-fill-primary/5"
            : "border-edge-gray-300 dark:border-edge-gray-600 bg-fill-white dark:bg-fill-deep-surface hover:border-edge-primary"
        }`}
      >
        <Upload className={`w-4 h-4 flex-shrink-0 ${isDragging ? "text-ink-primary" : "text-ink-gray-400"}`} />
        <span className={`text-sm ${isDragging ? "text-ink-primary" : "text-ink-gray-400"}`}>
          {placeholder}
        </span>
      </div>
      
      <input
        aria-label={inputLabel}
        ref={fileInputRef}
        type="file"
        disabled={disabled}
        accept={accept || require("../../utils/helpers").ALLOWED_FILE_ACCEPT_STRING}
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {error && (
        <p className="text-xs text-ink-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};

export default FileUploadInput;
